/**
 * JurisdictionsPage — Retirement jurisdiction tax comparison
 *
 * Compares 7 tax jurisdictions (UK, HK, SG, CY, MT, MO, PT) for both
 * drawdown tax efficiency and inheritance tax, helping model the financial
 * impact of relocating for retirement.
 */
import { useEffect, useRef, useState } from 'react'
import { Globe, TrendingDown, Trophy, RefreshCw } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '@/lib/api'
import TaxWorkspaceTabs from '@/components/ui/TaxWorkspaceTabs'
import EditableNumberSlider from '@/components/ui/EditableNumberSlider'
import { usePlanningAssumptions } from '@/hooks/usePlanningAssumptions'

type JurisdictionId = 'UK' | 'HK' | 'SG' | 'CY' | 'MT' | 'MO' | 'PT'

interface JurisdictionRow {
  jurisdiction_id: JurisdictionId
  name: string
  flag: string
  annual_drawdown_tax_gbp: number
  annual_drawdown_net_gbp: number
  annual_drawdown_effective_rate_pct: number
  iht_today_gbp: number
  iht_2027_gbp: number
  annual_saving_vs_uk_gbp: number
  decade_saving_vs_uk_gbp: number
  iht_saving_vs_uk_gbp: number
  total_20yr_prize_gbp: number
  notes: string
  dta_with_uk?: boolean
}

interface CompareAllResponse {
  gross_drawdown_gbp: number
  combined: JurisdictionRow[]
  drawdown_detail: Record<string, unknown>
  iht_detail: Record<string, unknown>
}

const fmt = (n: number) =>
  `£${n.toLocaleString('en-GB', { maximumFractionDigits: 0 })}`

const fmtPct = (n: number) => `${n.toFixed(1)}%`

export default function JurisdictionsPage() {
  const [grossDrawdown, setGrossDrawdown] = useState(220_000)
  const assumptions = usePlanningAssumptions((s) => s.assumptions)
  const fetchAssumptions = usePlanningAssumptions((s) => s.fetch)
  const hydratedRef = useRef(false)

  // Hydrate the slider from the shared planning store (canonical £220k gross)
  useEffect(() => {
    if (!assumptions) {
      void fetchAssumptions()
      return
    }
    if (!hydratedRef.current) {
      hydratedRef.current = true
      setGrossDrawdown(assumptions.target_drawdown_gbp ?? 220_000)
    }
  }, [assumptions, fetchAssumptions])
  const [data, setData] = useState<CompareAllResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [expandedRow, setExpandedRow] = useState<JurisdictionId | null>(null)

  const fetchData = async (gross: number) => {
    setLoading(true)
    setError(null)
    try {
      const res = await api.get<CompareAllResponse>('/api/jurisdictions/compare-all', {
        params: { gross_drawdown_gbp: gross },
      })
      setData(res.data)
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load jurisdiction comparison')
    } finally {
      setLoading(false)
    }
  }

  // Initial fetch
  useEffect(() => {
    fetchData(grossDrawdown)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Debounced re-fetch on slider change
  useEffect(() => {
    const t = setTimeout(() => fetchData(grossDrawdown), 250)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grossDrawdown])

  // Derived data
  const sorted = data
    ? [...data.combined].sort((a, b) => b.total_20yr_prize_gbp - a.total_20yr_prize_gbp)
    : []

  const winner = sorted[0] ?? null
  const uk = data?.combined.find(r => r.jurisdiction_id === 'UK') ?? null
  const biggestAnnualSaving = sorted.find(r => r.jurisdiction_id !== 'UK') ?? null
  const biggest20yr = sorted[0] ?? null

  if (loading && !data) {
    return (
      <div className="space-y-4 animate-fade-in">
        <div className="h-8 w-72 skeleton" />
        <div className="h-24 skeleton" />
        <div className="h-48 skeleton" />
        <div className="h-64 skeleton" />
      </div>
    )
  }

  if (error || (!loading && !data)) {
    return (
      <div className="card p-6 text-center">
        <p className="text-rose-light">{error ?? 'No data'}</p>
        <button
          type="button"
          onClick={() => fetchData(grossDrawdown)}
          className="btn-secondary mt-3"
        >
          Retry
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <Globe size={20} className="text-blue-light" aria-hidden />
            Jurisdiction Comparison — Where to Retire
          </h1>
          <p className="text-sm text-text-secondary">
            Models the full tax impact of relocating: drawdown income tax + inheritance tax across
            7 jurisdictions, versus the UK baseline.
          </p>
        </div>
        <button
          type="button"
          onClick={() => fetchData(grossDrawdown)}
          className="btn-secondary gap-2 text-xs"
        >
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} aria-hidden />
          Refresh
        </button>
      </div>

      <TaxWorkspaceTabs />

      {/* Slider */}
      <div className="card p-5">
        <div className="max-w-md">
          <EditableNumberSlider
            label="Gross annual drawdown"
            value={grossDrawdown}
            onChange={setGrossDrawdown}
            min={50_000}
            max={500_000}
            step={5_000}
            format={fmt}
          />
        </div>
        <p className="text-xs text-text-muted mt-2">
          Adjust the target gross drawdown to see how tax costs shift across jurisdictions.
        </p>
      </div>

      {/* Headline cards */}
      {data && winner && uk && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 stagger-children">
          {/* Winner */}
          <div className="stat-card card-hover border-emerald/30 bg-emerald/5">
            <p className="stat-label flex items-center gap-1">
              <Trophy size={11} className="text-emerald-light" aria-hidden /> Winner
            </p>
            <p className="stat-value text-emerald-light">
              {winner.flag} {winner.name}
            </p>
            <p className="text-xs text-text-muted">
              {fmtPct(winner.annual_drawdown_effective_rate_pct)} effective rate
            </p>
          </div>

          {/* UK Baseline */}
          <div className="stat-card border-rose/20 bg-rose/5">
            <p className="stat-label">UK Baseline</p>
            <p className="stat-value text-rose-light">{fmt(uk.annual_drawdown_tax_gbp)}/yr</p>
            <p className="text-xs text-text-muted">
              {fmtPct(uk.annual_drawdown_effective_rate_pct)} effective tax
            </p>
          </div>

          {/* Biggest annual saving */}
          {biggestAnnualSaving && (
            <div className="stat-card">
              <p className="stat-label flex items-center gap-1">
                <TrendingDown size={11} className="text-blue-light" aria-hidden /> Best Annual Saving
              </p>
              <p className="stat-value text-blue-light">
                +{fmt(biggestAnnualSaving.annual_saving_vs_uk_gbp)}/yr
              </p>
              <p className="text-xs text-text-muted">
                {biggestAnnualSaving.flag} {biggestAnnualSaving.name}
              </p>
            </div>
          )}

          {/* Biggest 20yr prize */}
          {biggest20yr && biggest20yr.jurisdiction_id !== 'UK' && (
            <div className="stat-card">
              <p className="stat-label">Best 20yr Total Prize</p>
              <p className="stat-hero text-amber-light">
                {fmt(biggest20yr.total_20yr_prize_gbp)}
              </p>
              <p className="text-xs text-text-muted">
                {biggest20yr.flag} {biggest20yr.name} over 20 years
              </p>
            </div>
          )}
          {biggest20yr && biggest20yr.jurisdiction_id === 'UK' && (
            <div className="stat-card">
              <p className="stat-label">Best 20yr Total Prize</p>
              <p className="stat-hero text-amber-light">
                {fmt(biggest20yr.total_20yr_prize_gbp)}
              </p>
              <p className="text-xs text-text-muted">
                {biggest20yr.flag} {biggest20yr.name}
              </p>
            </div>
          )}
        </div>
      )}

      {/* DTA warning box */}
      <div className="card p-4 border border-amber/30 bg-amber/5">
        <p className="text-xs font-semibold text-amber-light mb-1">
          ⚠ Double Tax Agreements (DTA) with the UK
        </p>
        <p className="text-xs text-text-secondary leading-relaxed">
          Jurisdictions without a Double Tax Agreement (DTA) with the UK cannot claim treaty relief on
          UK-source pension income — HMRC continues to tax SIPP drawdown at source at up to 45%
          regardless of the local tax rate. Macau is the key example: despite a 0% local income tax
          rate, the absence of a UK DTA means UK pension payments remain subject to full UK income tax
          at source.
        </p>
      </div>

      {/* Main comparison table */}
      {sorted.length > 0 && (
        <div className="card overflow-hidden">
          <div className="px-5 py-3 border-b border-border flex items-center justify-between">
            <h2 className="panel-title">Full Comparison</h2>
            <span className="text-xs text-text-muted">sorted by 20yr total prize</span>
          </div>
          <div className="table-responsive">
            <table className="w-full text-xs table-pro">
              <thead>
                <tr>
                  <th className="px-4">
                    Jurisdiction
                  </th>
                  <th className="!text-right">
                    Drawdown Tax/yr
                  </th>
                  <th className="!text-right">
                    Net Keep/yr
                  </th>
                  <th className="!text-right hidden sm:table-cell">
                    Effective Rate
                  </th>
                  <th className="!text-right hidden md:table-cell">
                    vs UK/yr
                  </th>
                  <th className="!text-right hidden md:table-cell">
                    IHT 2027+
                  </th>
                  <th className="!text-right px-4">
                    20yr Prize
                  </th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((row, i) => {
                  const isWinner = i === 0
                  const isUK = row.jurisdiction_id === 'UK'
                  const isExpanded = expandedRow === row.jurisdiction_id

                  return (
                    <>
                      <tr
                        key={row.jurisdiction_id}
                        className={clsx(
                          'cursor-pointer transition-colors',
                          isWinner && 'bg-emerald/10 hover:bg-emerald/15',
                          isUK && !isWinner && 'bg-rose/5 hover:bg-rose/10',
                        )}
                        onClick={() =>
                          setExpandedRow(isExpanded ? null : row.jurisdiction_id)
                        }
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="text-lg" role="img" aria-label={row.name}>
                              {row.flag}
                            </span>
                            <div>
                              <p
                                className={clsx(
                                  'font-semibold',
                                  isWinner ? 'text-emerald-light' : 'text-text-primary',
                                )}
                              >
                                {row.name}
                              </p>
                              <div className="flex items-center gap-1 flex-wrap mt-0.5">
                                {isWinner && (
                                  <span className="badge badge-green text-[10px]">WINNER</span>
                                )}
                                {isUK && !isWinner && (
                                  <span className="badge badge-red text-[10px]">BASELINE</span>
                                )}
                                {row.dta_with_uk === true && (
                                  <span className="badge badge-green text-[10px]">DTA</span>
                                )}
                                {row.dta_with_uk === false && (
                                  <span className="badge badge-red text-[10px]">NO DTA</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="text-right px-3 py-3 font-mono text-rose-light">
                          {fmt(row.annual_drawdown_tax_gbp)}
                        </td>
                        <td className="text-right px-3 py-3 font-mono font-semibold text-emerald-light">
                          {fmt(row.annual_drawdown_net_gbp)}
                        </td>
                        <td className="text-right px-3 py-3 font-mono text-text-secondary hidden sm:table-cell">
                          {fmtPct(row.annual_drawdown_effective_rate_pct)}
                        </td>
                        <td className="text-right px-3 py-3 font-mono hidden md:table-cell">
                          {row.annual_saving_vs_uk_gbp > 0 ? (
                            <span className="text-emerald-light">
                              +{fmt(row.annual_saving_vs_uk_gbp)}
                            </span>
                          ) : row.annual_saving_vs_uk_gbp < 0 ? (
                            <span className="text-rose-light">
                              {fmt(row.annual_saving_vs_uk_gbp)}
                            </span>
                          ) : (
                            <span className="text-text-muted">—</span>
                          )}
                        </td>
                        <td className="text-right px-3 py-3 font-mono text-text-secondary hidden md:table-cell">
                          {fmt(row.iht_2027_gbp)}
                        </td>
                        <td className="text-right px-4 py-3 font-mono font-bold text-text-primary">
                          {row.total_20yr_prize_gbp > 0
                            ? fmt(row.total_20yr_prize_gbp)
                            : <span className="text-text-muted">—</span>
                          }
                        </td>
                      </tr>

                      {/* Expanded notes row */}
                      {isExpanded && (
                        <tr
                          key={`${row.jurisdiction_id}-notes`}
                          className={clsx(
                            'border-b border-border',
                            isWinner && 'bg-emerald/5',
                            isUK && !isWinner && 'bg-rose/5',
                          )}
                        >
                          <td colSpan={7} className="px-4 py-3">
                            <div className="text-xs text-text-secondary leading-relaxed bg-surface-card rounded-lg p-3 border border-border">
                              <p className="text-text-muted font-semibold mb-1 uppercase tracking-wider text-[10px]">
                                Notes — {row.name}
                              </p>
                              <p>{row.notes}</p>
                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3 pt-3 border-t border-border">
                                <div>
                                  <p className="text-text-muted">IHT today</p>
                                  <p className="font-bold text-text-primary">{fmt(row.iht_today_gbp)}</p>
                                </div>
                                <div>
                                  <p className="text-text-muted">IHT saving vs UK</p>
                                  <p className="font-bold text-emerald-light">
                                    +{fmt(row.iht_saving_vs_uk_gbp)}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-text-muted">Decade saving vs UK</p>
                                  <p className="font-bold text-emerald-light">
                                    +{fmt(row.decade_saving_vs_uk_gbp)}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-text-muted">Effective rate</p>
                                  <p className="font-bold text-text-primary">
                                    {fmtPct(row.annual_drawdown_effective_rate_pct)}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  )
                })}
              </tbody>
            </table>
          </div>
          <p className="px-4 py-2 text-[11px] text-text-muted border-t border-border">
            Click any row to expand notes. 20yr Prize = decade drawdown saving + IHT saving vs UK.
          </p>
        </div>
      )}

      {/* Strategic context callout */}
      <div className="card p-5 border-blue/20 bg-blue/5">
        <h2 className="panel-title mb-2 flex items-center gap-2">
          <Globe size={14} className="text-blue-light" aria-hidden />
          Strategic Context
        </h2>
        <p className="text-sm text-text-secondary leading-relaxed">
          Macau is particularly strategic because Unman holds Macau citizenship, making residency
          automatic. Unman also holds Portuguese citizenship (EU automatic residency). Cyprus, Malta,
          and Portugal offer EU residency options with favourable tax regimes. Note: Portugal's
          NHR (Non-Habitual Resident) regime closed to new applicants on 1 January 2024 —
          new retirees face the full progressive scale (up to 48%).
        </p>
      </div>
    </div>
  )
}
